<template>
	<div>
		<div class="section-title">Permitted uses</div>
		<AppRadioButton v-model="permittedUse" :choices="permittedUses">
			<template v-slot:label="{ item }">
				<MaybeUpgradeRequiredIndicator :billingFeatureId="item.billingFeatureId" v-if="item.billingFeatureId">
					<template #default="{ indicator, isUpgradeRequired }">
						<GroupPermissionItem :item="item" :indicator="indicator" :isUpgradeRequired="isUpgradeRequired" />
					</template>
				</MaybeUpgradeRequiredIndicator>
				<GroupPermissionItem v-else :item="item" />
			</template>
		</AppRadioButton>
		<div v-if="showPreferences" class="display-flex flex-column mt-n4 ml-3">
			<AppCheckbox
				:key="i"
				class="mt-n1"
				:ripple="false"
				:label="preference.title"
				:isOn="preference.value"
				@update:isOn="(value) => updatePreference(i, value)"
				v-for="(preference, i) in preferences"
			/>
		</div>
	</div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import GroupPermissionItem from './GroupPermissionItem.vue';
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import { createGroup, updateGroup } from '@/store/modules/accountGroups/actions';
import { groups, defaultGroupName } from '@/store/modules/accountGroups/getters';
import { useHasCapability } from '@/store/modules/billing/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { GROUP_PERMITTED_USES } from '@/utils/groups-const';
const initialUsages = {
	//naming based on the server names - keep snake-case
	collaborate_project: true,
	share_workflow: true,
	send_notification: true,
};
export default {
	name: 'GroupPermittedUses',
	components: { MaybeUpgradeRequiredIndicator, GroupPermissionItem },
	props: {
		group: { type: Object },
	},
	computed: {
		...mapGetters([groups.fullPath, defaultGroupName.fullPath]),
		...mapGettersShorthand({ useHasCapability }),
		permittedUses() {
			return [
				{
					title: `All uses allowed`,
					value: GROUP_PERMITTED_USES.ALL,
				},
				{
					title: `Choose specific uses`,
					value: GROUP_PERMITTED_USES.PREFERENCES,
					billingFeatureId: FeatureTypes.GroupUsagePermissions,
					disabled: !this.useHasCapability(FeatureTypes.GroupUsagePermissions),
				},
			];
		},
		showPreferences() {
			return this.permittedUse === GROUP_PERMITTED_USES.PREFERENCES;
		},
		preferences() {
			return [
				{
					title: 'Allow sending notifications to this group',
					subTitle: '(Shared inbox)',
					value: this.usePreferences.send_notification,
					key: 'send_notification',
				},
				{
					title: 'Allow sharing features with this group',
					value: this.usePreferences.share_workflow,
					key: 'share_workflow',
				},
				{
					title: 'Allow adding this group as collaborators',
					value: this.usePreferences.collaborate_project,
					key: 'collaborate_project',
				},
			];
		},
	},
	methods: {
		...mapActions([createGroup.fullPath, updateGroup.fullPath]),
		updatePreference(i, value) {
			Vue.set(this.usePreferences, this.preferences[i].key, value);
		},
		areAllPreferencesSame(x, y) {
			return Object.keys(x).every((key) => x[key] == y[key]);
		},
	},
	async mounted() {
		if (this.group?.usage_preferences) {
			this.permittedUse = Object.values(this.group.usage_preferences).every((x) => x === true)
				? GROUP_PERMITTED_USES.ALL
				: GROUP_PERMITTED_USES.PREFERENCES;
			this.usePreferences = { ...this.group.usage_preferences };
		}
	},
	data: () => ({
		permittedUse: undefined,
		usePreferences: { ...initialUsages },
	}),
	watch: {
		usePreferences: {
			handler(val) {
				val && this.$emit('selected-permitted-use', val);
				if (this.areAllPreferencesSame(val, initialUsages)) {
					this.permittedUse = GROUP_PERMITTED_USES.ALL;
				}
			},
			immediate: true,
			deep: true,
		},
		permittedUse: {
			handler(val) {
				if (val === GROUP_PERMITTED_USES.ALL && this.usePreferences !== initialUsages) {
					for (const key of Object.keys(this.usePreferences)) {
						Vue.set(this.usePreferences, key, true);
					}
				}
			},
			immediate: true,
		},
	},
};
</script>

<style scoped lang="scss">
.group-body {
	height: 300px !important;
	overflow: auto !important;
}
.save-button {
	width: 144px !important;
	height: 38px !important;
	font-size: 14px !important;
	border-radius: 3px !important;
	letter-spacing: 0px !important;
	color: var(--white) !important;
	font-weight: normal !important;
	border-color: transparent !important;
	background: var(--forth-color) !important;
}
.label {
	font-size: 14px;
	color: var(--primary-font-color);
}
.section-title {
	@extend .label;
	font-weight: medium;
}
::v-deep {
	.v-icon {
		font-size: 14px !important;
	}
	.v-label {
		font-size: 14px !important;
	}
	.v-input--selection-controls__input {
		margin-right: 0px !important;
	}
}
</style>
